import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, MenuItem, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, Pagination, CircularProgress, Alert, Stack, Tooltip,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import apiService from '../services/api';

const eur = (n: number) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);

const StatCard: React.FC<{ label: string; value: string | number; color?: string }> = ({ label, value, color }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 160 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, color }}>{value}</Typography>
  </Paper>
);

const SearchLog: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [portals, setPortals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [portalId, setPortalId] = useState('');
  const [cacheHit, setCacheHit] = useState('');
  const pageSize = 50;

  useEffect(() => {
    apiService.listPortals().then((d) => setPortals(d.portals || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params: any = { page, page_size: pageSize };
      if (portalId) params.portal_id = portalId;
      if (cacheHit) params.cache_hit = cacheHit;
      setData(await apiService.getCalls(params));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Errore nel caricamento del log');
    } finally {
      setLoading(false);
    }
  }, [page, portalId, cacheHit]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals;
  const calls = data?.calls || [];
  const pages = data?.pagination?.pages || 1;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">Log Ricerche</Typography>
        <Typography color="text.secondary">Ogni ricerca dei clienti con costo fatturato, costo upstream e margine</Typography>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <TextField select label="Cliente (portale)" size="small" sx={{ minWidth: 220 }}
          value={portalId} onChange={(e) => { setPage(1); setPortalId(e.target.value); }}>
          <MenuItem value="">Tutti</MenuItem>
          {portals.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </TextField>
        <TextField select label="Cache" size="small" sx={{ minWidth: 180 }}
          value={cacheHit} onChange={(e) => { setPage(1); setCacheHit(e.target.value); }}>
          <MenuItem value="">Tutte</MenuItem>
          <MenuItem value="true">Solo cache hit</MenuItem>
          <MenuItem value="false">Solo cache miss</MenuItem>
        </TextField>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <StatCard label="Ricerche" value={totals?.count ?? 0} />
        <StatCard label="Cliente ha pagato" value={eur(totals?.client_paid)} />
        <StatCard label="Costo per noi" value={eur(totals?.our_cost)} />
        <StatCard label="Margine" value={eur(totals?.margin)} color="success.main" />
      </Stack>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6 }}>
            <CircularProgress /><Typography color="text.secondary">Caricamento…</Typography>
          </Box>
        ) : calls.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
            Nessuna ricerca registrata per i filtri selezionati.
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data/ora</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>User ref</TableCell>
                    <TableCell>Endpoint</TableCell>
                    <TableCell align="center">Cache</TableCell>
                    <TableCell align="right">Risultati</TableCell>
                    <TableCell align="right">Cliente ha pagato</TableCell>
                    <TableCell align="right">Costo per noi</TableCell>
                    <TableCell align="right">Margine</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {calls.map((c: any) => (
                    <TableRow key={c.id} hover>
                      <TableCell>{new Date(c.created_at).toLocaleString('it-IT')}</TableCell>
                      <TableCell><strong>{c.portal_name}</strong></TableCell>
                      <TableCell>{c.user_reference || '—'}</TableCell>
                      <TableCell><code>{c.endpoint}</code></TableCell>
                      <TableCell align="center">
                        <Tooltip title={c.cache_hit ? 'Servita dalla cache — nessun costo upstream' : 'Chiamata al provider dati — a nostro costo'}>
                          <FiberManualRecordIcon fontSize="small" sx={{ color: c.cache_hit ? 'success.main' : 'error.main', verticalAlign: 'middle' }} />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">{c.results_count ?? '—'}</TableCell>
                      <TableCell align="right">{eur(c.client_paid)}</TableCell>
                      <TableCell align="right">{eur(c.our_cost)}</TableCell>
                      <TableCell align="right" sx={{ color: c.margin > 0 ? 'success.main' : 'inherit', fontWeight: 600 }}>{eur(c.margin)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <Pagination color="primary" count={pages} page={page} disabled={loading} onChange={(_, p) => setPage(p)} />
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default SearchLog;
